<template>
    <div class="odc-config">
        <!-- Same spinner the parent shows while /get-od-data is in flight. -->
        <div v-if="loading" class="loading-indicator">
            <i class="fa fa-spinner fa-spin mr-2"></i>
            <span>Loading diseases...</span>
        </div>

        <template v-else>
            <p class="odc-intro">
                Patients first tap a category, then tick the specific conditions under it.
                Assign each Open Dental disease to one or more categories below.
            </p>

            <!-- ============================ Categories ============================ -->
            <div class="odc-category"
                 v-for="(cat, index) in control.categories"
                 :key="cat.id">

                <div class="odc-category-head">
                    <span class="odc-category-index">Category {{ index + 1 }}</span>
                    <span class="odc-category-tools">
                        <button type="button"
                                class="btn btn-sm btn-outline-secondary"
                                :disabled="index === 0"
                                @click="moveCategory(index, -1)">Move up</button>
                        <button type="button"
                                class="btn btn-sm btn-outline-secondary"
                                :disabled="index === control.categories.length - 1"
                                @click="moveCategory(index, 1)">Move down</button>
                        <button type="button"
                                class="btn btn-sm btn-outline-danger"
                                @click="removeCategory(index)">Remove</button>
                    </span>
                </div>

                <div :class="styles.FORM.FORM_GROUP">
                    <label>Category name</label>
                    <input type="text"
                           :class="styles.FORM.FORM_CONTROL"
                           placeholder="Category name"
                           v-model="cat.name">
                </div>

                <div :class="styles.FORM.FORM_GROUP"
                     v-for="lang in enabledTranslationLanguages"
                     :key="cat.id + '-name-' + lang.code">
                    <label>Category name ({{ lang.name }})</label>
                    <input type="text"
                           :class="styles.FORM.FORM_CONTROL"
                           :placeholder="lang.name + ' category name'"
                           v-model="cat['name' + lang.suffix]">
                </div>

                <div :class="styles.FORM.FORM_GROUP">
                    <label>Conditions in this category</label>
                    <multiselect
                        v-model="cat.options"
                        :options="allOptions"
                        :multiple="true"
                        :taggable="false"
                        :show-labels="false"
                        label="text"
                        track-by="value"
                        placeholder="Pick the conditions to show under this category"
                    />
                </div>
            </div>

            <div class="odc-category-actions">
                <button type="button"
                        class="btn btn-sm btn-outline-primary"
                        @click="addCategory">Add category</button>
                <button type="button"
                        class="btn btn-sm btn-outline-secondary"
                        @click="restoreDefaults">Restore default categories</button>
                <!-- Onboarding aid: sorts only what is still Unassigned, never moves or removes
                     anything, so it is safe to press again. Hidden when there is nothing to sort
                     or nowhere to sort it to ("Other Conditions" / the None tile are excluded). -->
                <button type="button"
                        class="btn btn-sm btn-outline-primary odc-auto-assign"
                        v-if="unassigned.length > 0 && assignableCategories.length > 0"
                        :disabled="autoAssigning"
                        @click="autoAssignUnassigned">
                    <template v-if="autoAssigning">
                        <i class="fa fa-spinner fa-spin mr-1"></i>Sorting {{ unassigned.length }} conditions&hellip;
                    </template>
                    <template v-else>Auto-assign unassigned (AI)</template>
                </button>
            </div>

            <!-- ======================= Unassigned diseases ======================== -->
            <div class="odc-unassigned">
                <label>Unassigned diseases ({{ unassigned.length }})</label>

                <p class="odc-hint" v-if="allOptions.length === 0">
                    No diseases came back from Open Dental for this site.
                </p>
                <p class="odc-hint" v-else-if="unassigned.length === 0">
                    Every Open Dental disease is assigned.
                </p>
                <div class="odc-chips" v-else>
                    <span class="odc-chip"
                          v-for="option in unassigned"
                          :key="'unassigned-' + option.value">
                        <span class="odc-chip-text" v-text="option.text"></span>
                        <select class="odc-chip-select"
                                @change="onAssignSelect(option, $event)">
                            <option value="">Add to&hellip;</option>
                            <option v-for="cat in control.categories"
                                    :key="'assign-' + option.value + '-' + cat.id"
                                    :value="cat.id"
                                    v-text="cat.name || '(unnamed)'">
                            </option>
                        </select>
                    </span>
                </div>
            </div>

            <!-- ========================= "No conditions" ========================== -->
            <div class="odc-none">
                <label class="odc-checkbox">
                    <input type="checkbox" v-model="control.showNoneChoice">
                    <span>Offer a "No conditions" choice (stored as None &mdash; clears the patient's active problems in Open Dental)</span>
                </label>

                <template v-if="control.showNoneChoice">
                    <div :class="styles.FORM.FORM_GROUP">
                        <label>"No conditions" label</label>
                        <input type="text"
                               :class="styles.FORM.FORM_CONTROL"
                               :placeholder="defaultNoneLabel"
                               v-model="control.noneLabel">
                    </div>

                    <div :class="styles.FORM.FORM_GROUP"
                         v-for="lang in enabledTranslationLanguages"
                         :key="'none-' + lang.code">
                        <label>"No conditions" label ({{ lang.name }})</label>
                        <input type="text"
                               :class="styles.FORM.FORM_CONTROL"
                               :placeholder="lang.name + ' label'"
                               v-model="control['noneLabel' + lang.suffix]">
                    </div>
                </template>
            </div>

            <small class="odc-footnote">
                Save to Open Dental Field must be Diseases (or blank for PDF-only). Open Dental's
                "Other" problem is never a checkbox: in a category holding only "Other" (it starts in
                Other Conditions), tapping the category opens a required "Please describe" box; in a
                category with other conditions, patients get an optional box under the checkboxes.
                The description is saved to Other Problems in Open Dental &mdash; do not add a
                separate text box mapped to Other Problems.
            </small>
        </template>
    </div>
</template>

<script>
    /**
     * Builder panel for the 5th Open Dental data type, "DISEASES with Category and Checkbox
     * Options". Rendered INSIDE OpenDentalDataConfigView.vue when the control's dataType is
     * DISEASES_CATEGORIZED.
     *
     * WHAT IT OWNS
     * `control.categories` (the ordered category rows and the diseases under each),
     * `control.showNoneChoice` / `control.noneLabel` (+ the per-language label keys), and
     * `control.selectedOptions`, which it keeps equal to the deduped UNION of every category's
     * options. That union is the reason the rest of the builder needs no changes at all: the
     * parent's alert-options picker, its alertOptions pruning watcher and ViewForm's "Other"
     * lookup all read `selectedOptions` and keep working untouched.
     *
     * ⛔ IDS MUST PERSIST. od-categorized-diseases.js hands a FRESH random id to any category
     * row that has none, on EVERY call, and the patient side keys its ticked/open state by id.
     * So normalizeCategories() runs ONCE in created() and the result is written back to the
     * control; every row added later gets its id from newCategoryId(). Never re-normalize into
     * the render path.
     */
    import {CONTROL_SPECIAL_CONFIG_MIXIN} from "../../mixins/control-special-config-mixin";
    import {
        DEFAULT_NONE_LABEL,
        applyAssignments,
        assignableCategories,
        newCategoryId,
        normalizeCategories,
        placeOtherInDefaultCategory,
        restoreDefaultCategories,
        seedDefaultCategories,
        unassignedOptions,
        unionCategoryOptions
    } from "../../libraries/od-categorized-diseases";
    import Multiselect from "vue-multiselect";
    import {getIdToken} from "@/pages/MyAccount/components/firebaseIdToken";

    export default {
        name: "DiseasesCategorizedConfig",
        components: { Multiselect },
        mixins: [CONTROL_SPECIAL_CONFIG_MIXIN],

        props: {
            /**
             * Every disease Open Dental returned for this site — the parent's `radioOptions`,
             * `[{text, value}]` with value = DiseaseDefNum.
             */
            allOptions: {
                type: Array,
                default: () => []
            },

            /**
             * True while the parent is still fetching that list.
             */
            loading: {
                type: Boolean,
                default: false
            }
        },

        data: () => ({
            // A brand-new control seeded before the site's Open Dental list arrived: Open Dental's
            // "Other" still has to be put into "Other Conditions" once the list is here.
            pendingOtherPlacement: false,

            // Same base as the parent's /get-od-data fetch — one env var, no hardcoded URL.
            autoAssignURL: `${process.env.VUE_APP_TEAMIO_API_URL}/categorize-diseases`,

            // True while POST /categorize-diseases is in flight (the button shows a spinner).
            autoAssigning: false
        }),

        watch: {
            /**
             * The site's Open Dental list can arrive after created() (it is fetched by the parent).
             * Places "Other" for a brand-new control once, then never again — after that the
             * office owns where "Other" sits.
             */
            allOptions() {
                this.placeOtherIfPending();
            },

            /**
             * `control.selectedOptions` is DERIVED: it must stay the deduped union of the
             * categories, whatever edits the office makes (rename, reorder, add, remove, or a
             * pick inside one of the multiselects — hence `deep`).
             */
            'control.categories': {
                deep: true,
                handler() {
                    this.ensureLanguageKeys();
                    this.syncSelectedOptions();
                }
            },

            /**
             * Turning Spanish on in Form Configuration while this panel is open must give every
             * existing category (and the None label) a reactive translation key.
             */
            enabledTranslationLanguages() {
                this.ensureLanguageKeys();
            }
        },

        created() {
            // Vue 2 reactivity: these live on the control, which was saved before they existed.
            const existing = this.control.categories;
            if (!Array.isArray(existing) || existing.length === 0) {
                // Open Dental's "Other" starts in "Other Conditions" (it gets the required
                // "Please describe" box on the patient side). If the list is still loading, the
                // allOptions watcher places it on arrival.
                this.$set(this.control, 'categories', seedDefaultCategories(this.allOptions));
                this.pendingOtherPlacement = !Array.isArray(this.allOptions) || this.allOptions.length === 0;
            } else {
                // Written back so the generated ids PERSIST — see the ⛔ note above.
                this.$set(this.control, 'categories', normalizeCategories(existing));
            }

            if (!Object.prototype.hasOwnProperty.call(this.control, 'showNoneChoice')) {
                this.$set(this.control, 'showNoneChoice', true);
            }
            if (!Object.prototype.hasOwnProperty.call(this.control, 'noneLabel')) {
                this.$set(this.control, 'noneLabel', DEFAULT_NONE_LABEL);
            }

            this.ensureLanguageKeys();
            this.syncSelectedOptions();
        },

        methods: {
            /**
             * Give every enabled language a reactive `nameXx` key on each category and a
             * `noneLabelXx` key on the control. v-model alone would write a NON-reactive
             * property in Vue 2 and the input would not survive a re-render.
             */
            ensureLanguageKeys() {
                const languages = this.enabledTranslationLanguages;
                if (!languages.length) return;

                const categories = Array.isArray(this.control.categories) ? this.control.categories : [];
                languages.forEach(lang => {
                    const noneKey = 'noneLabel' + lang.suffix;
                    if (!Object.prototype.hasOwnProperty.call(this.control, noneKey)) {
                        this.$set(this.control, noneKey, '');
                    }
                    const nameKey = 'name' + lang.suffix;
                    categories.forEach(cat => {
                        if (!Object.prototype.hasOwnProperty.call(cat, nameKey)) {
                            this.$set(cat, nameKey, '');
                        }
                    });
                });
            },

            /**
             * Rewrite `control.selectedOptions` as the union, but ONLY when it actually
             * changed: the parent watches selectedOptions to prune alertOptions, so a churning
             * identical array would be pointless work on every keystroke in a name input.
             */
            syncSelectedOptions() {
                const union = unionCategoryOptions(this.control.categories);
                if (JSON.stringify(union) === JSON.stringify(this.control.selectedOptions)) return;
                this.$set(this.control, 'selectedOptions', union);
            },

            /**
             * A new, empty category at the end of the list.
             */
            addCategory() {
                const taken = (Array.isArray(this.control.categories) ? this.control.categories : [])
                    .map(cat => cat.id);
                this.control.categories.push({
                    id: newCategoryId(taken),
                    name: '',
                    options: []
                });
                this.ensureLanguageKeys();
            },

            /**
             * Remove a category. Losing an assignment list the office built by hand is worth a
             * confirm; an empty row is not.
             */
            removeCategory(index) {
                const cat = this.control.categories[index];
                if (!cat) return;
                const assigned = Array.isArray(cat.options) ? cat.options.length : 0;
                if (assigned > 0) {
                    const name = cat.name || 'this category';
                    const message = `Remove "${name}"? The ${assigned} condition(s) assigned to it will stop being offered to patients.`;
                    if (!window.confirm(message)) return;
                }
                this.control.categories.splice(index, 1);
            },

            /**
             * Move a category one place up (-1) or down (+1). Category order is the order the
             * patient sees the tiles in, and the order the submission view groups by.
             */
            moveCategory(index, delta) {
                const target = index + delta;
                if (target < 0 || target >= this.control.categories.length) return;
                const moved = this.control.categories.splice(index, 1)[0];
                this.control.categories.splice(target, 0, moved);
            },

            /**
             * Append every shipped default whose NAME is missing. Renamed, reordered and extra
             * categories are never touched or removed. Open Dental's "Other" is put back into
             * "Other Conditions" if it is in no category.
             */
            restoreDefaults() {
                this.$set(this.control, 'categories', restoreDefaultCategories(this.control.categories, this.allOptions));
                this.ensureLanguageKeys();
            },

            /**
             * See `pendingOtherPlacement`. The categories already carry persisted ids here, which
             * placeOtherInDefaultCategory keeps (it only mints ids for id-less rows).
             */
            placeOtherIfPending() {
                if (!this.pendingOtherPlacement) return;
                if (!Array.isArray(this.allOptions) || this.allOptions.length === 0) return;
                this.pendingOtherPlacement = false;
                this.$set(this.control, 'categories', placeOtherInDefaultCategory(this.control.categories, this.allOptions));
            },

            /**
             * The Unassigned chip's "Add to…" select: assign, then reset the select so the same
             * chip can be assigned to a second category straight away.
             */
            onAssignSelect(option, event) {
                const categoryId = event && event.target ? event.target.value : '';
                if (event && event.target) event.target.value = '';
                if (!categoryId) return;
                this.assignToCategory(option, categoryId);
            },

            /**
             * Put one disease into one category, unless it is already there. Deduping by
             * String(value) matters all the way down: a duplicate DiseaseDefNum would
             * double-INSERT into Open Dental's `disease` table.
             */
            assignToCategory(option, categoryId) {
                const cat = (Array.isArray(this.control.categories) ? this.control.categories : [])
                    .find(row => row.id === categoryId);
                if (!cat || !option) return;
                if (!Array.isArray(cat.options)) this.$set(cat, 'options', []);
                const key = String(option.value);
                if (cat.options.some(opt => opt && String(opt.value) === key)) return;
                cat.options.push({ text: option.text, value: option.value });
            },

            /**
             * "Auto-assign unassigned (AI)": ask forms-service to sort the still-unassigned
             * diseases into the office's own categories (POST /categorize-diseases, same base and
             * fetch style as the parent's /get-od-data call).
             *
             * ⛔ Nothing is ever removed or moved. Only diseases in NO category are sent, only the
             * assignable categories are offered ("Other Conditions" / a "No conditions" tile are
             * excluded by EXCLUDED_CATEGORY_RX on BOTH sides), and the result is committed through
             * assignToCategory() — the very same push the manual "Add to…" select does, so the
             * deep watcher rebuilds `selectedOptions` and marks the form dirty identically.
             * Any failure leaves the config untouched.
             */
            async autoAssignUnassigned() {
                if (this.autoAssigning) return;

                const diseases = this.unassigned.map(opt => ({ value: opt.value, text: opt.text }));
                const categories = this.assignableCategories;
                if (diseases.length === 0 || categories.length === 0) {
                    this.showAutoAssignResult(0);
                    return;
                }

                this.autoAssigning = true;
                try {
                    // /categorize-diseases verifies this token and that its siteID matches the body's.
                    const idToken = await getIdToken(this.$store.state.getAuth ? this.$store.state.getAuth() : null);
                    const headers = idToken ? {Authorization: `Bearer ${idToken}`} : {};
                    const response = await fetch(this.autoAssignURL, {
                        method: "POST",
                        headers,
                        body: JSON.stringify({ siteID: this.siteID, categories, diseases })
                    });
                    if (!response.ok) {
                        throw new Error(`/categorize-diseases responded ${response.status}`);
                    }
                    const payload = await response.json();
                    const assignments = payload && Array.isArray(payload.assignments) ? payload.assignments : [];

                    const result = applyAssignments(this.control.categories, assignments, this.allOptions);
                    // Replay the accepted assignments through the manual path. assignToCategory()
                    // skips anything already there, so only the new items are pushed.
                    result.categories.forEach(cat => {
                        (Array.isArray(cat.options) ? cat.options : [])
                            .forEach(option => this.assignToCategory(option, cat.id));
                    });

                    this.showAutoAssignResult(result.assigned);
                } catch (error) {
                    console.error('[DiseasesCategorizedConfig] auto-assign failed', error);
                    this.$bvToast.toast('Auto-assign failed — nothing changed', {
                        title: 'Auto-assign', variant: 'danger', solid: true
                    });
                } finally {
                    this.autoAssigning = false;
                }
            },

            /**
             * The counts toast. `unassigned` is a computed over the categories just mutated, so it
             * already reports what is LEFT for the office to place by hand.
             */
            showAutoAssignResult(assigned) {
                const left = this.unassigned.length;
                this.$bvToast.toast(`Assigned ${assigned} conditions; ${left} left for you`, {
                    title: 'Auto-assign',
                    variant: assigned > 0 ? 'success' : 'info',
                    solid: true
                });
            }
        },

        computed: {
            /**
             * Every offered disease that is in no category yet.
             */
            unassigned() {
                return unassignedOptions(this.allOptions, this.control.categories);
            },

            /**
             * The categories the auto-assign may target — named, and not "Other Conditions" or a
             * "No conditions"/"None" tile. No assignable category ⇒ no button.
             */
            assignableCategories() {
                return assignableCategories(this.control.categories);
            },

            /**
             * Placeholder for the None caption input.
             */
            defaultNoneLabel: () => DEFAULT_NONE_LABEL
        }
    }
</script>

<style lang="scss" scoped>
.odc-intro {
    margin-bottom: 14px;
    color: #495057;
    font-size: 13px;
    line-height: 1.5;
}

.loading-indicator {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    background-color: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 4px;
    color: #6c757d;
    font-size: 14px;

    i {
        color: #007bff;
        font-size: 16px;
    }
}

.odc-category {
    margin-bottom: 14px;
    padding: 12px;
    background-color: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 6px;
}

/*
 * The builder sidebar gives EVERY .multiselect `position: relative; z-index: 50`
 * (css/v-form-builder-modern.css, "Fix z-index and overflow"). That makes each picker its own
 * stacking context at the SAME level, and when levels tie the LATER element paints on top — so the
 * NEXT category's picker (and the alert picker below the last category) showed THROUGH an open
 * option list (reported by Nilay with a screenshot, 2026-09-17). The dropdown's own
 * `z-index: 1000 !important` cannot help: it only ranks inside its picker's context.
 * Lift just the OPEN picker above its siblings. The selector out-ranks the sidebar rule (0,3,0).
 */
.odc-config .odc-category .multiselect.multiselect--active {
    z-index: 60;
}

.odc-category-head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    margin-bottom: 8px;
}

.odc-category-index {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    color: #6c757d;
}

.odc-category-tools {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 6px;
}

.odc-category-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 18px;
}

.odc-unassigned,
.odc-none {
    margin-bottom: 18px;
}

.odc-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.odc-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 100%;
    padding: 4px 8px;
    background-color: #ffffff;
    border: 1px solid #ced4da;
    border-radius: 14px;
    font-size: 12px;
}

.odc-chip-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/*
 * Specificity + explicit box on purpose. Two GLOBAL rules fight this small select:
 *   - `.vue-form-builder .sidebar-form-configuration select` (css/v-form-builder-modern.css) sets
 *     width: 100% and padding: 10px 14px, and out-ranks a single scoped class;
 *   - an app-wide unscoped `select { height: 20px }` (Conversations.vue) caps the box.
 * Together they leave a zero-height content area, so "Add to…" was clipped away and every chip
 * showed an EMPTY dropdown (seen in the 2026-09-17 Playwright screenshots). Pin height,
 * line-height, padding and width here so the placeholder is always readable.
 */
.odc-config .odc-chip .odc-chip-select {
    width: auto;
    min-width: 72px;
    max-width: 130px;
    height: 24px;
    line-height: 1.2;
    padding: 2px 6px;
    border: 1px solid #ced4da;
    border-radius: 10px;
    background-color: #ffffff;
    font-size: 11px;
    color: #495057;
}

.odc-checkbox {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 10px;
    font-size: 13px;
    line-height: 1.4;
    cursor: pointer;

    input[type="checkbox"] {
        width: 16px;
        height: 16px;
        margin-top: 2px;
        cursor: pointer;
    }
}

.odc-hint {
    margin: 0;
    color: #6c757d;
    font-size: 12px;
}

.odc-footnote {
    display: block;
    color: #6c757d;
    font-size: 12px;
    line-height: 1.4;
}
</style>
